"use client";

import { useRef, type RefObject } from "react";

import { trackAgentRevenueOfferShown } from "@/lib/analytics-agent-offers";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import { useImpressionWhenVisible } from "@/hooks/useImpressionOnce";

/**
 * OfferImpression — a near-render-nothing client island that fires the
 * `offer_seen` (`agent_revenue_offer_shown`) impression EXACTLY ONCE per offer
 * presentation, AND ONLY ONCE THE OFFER IS GENUINELY SEEN. Part of the
 * offer-placement architecture (ADR-0025).
 *
 * Why this exists: every report CTA needs the same mirrored `agent_revenue_*` +
 * `commerce_*` shown payload and the same re-fire protection. Server-rendered
 * offer surfaces (e.g. CalculatorReportBridge, used by muhurat / dasha /
 * sade-sati) also need a tiny client island so they can emit the event without
 * converting the whole surface to a client component. Re-fire-proof by
 * construction via {@link useImpressionWhenVisible} (gated on a stable string
 * key, never object identity — the 2026-06 re-fire incident class) and on real
 * VISIBILITY (a 0×0 sentinel observed by IntersectionObserver), so a below-the-
 * fold offer the seeker never scrolled to no longer inflates `offer_seen`.
 */
export default function OfferImpression({
  planId,
  sourcePath,
  landingPath,
  landingIntent,
  intent,
  placement,
  customerProblem,
  urgency = "medium",
  confidence,
  routeRankScore,
  routeRankReason,
  rankedOfferOrder,
  presentationKey,
  strongestDoshaId,
  strongestDoshaSeverity,
  ready,
  targetRef,
}: {
  planId: string;
  sourcePath: string;
  landingPath?: string;
  landingIntent?: string;
  intent: string;
  placement: string;
  customerProblem?: string;
  urgency?: string;
  confidence?: number;
  routeRankScore?: number;
  routeRankReason?: string;
  rankedOfferOrder?: string[];
  presentationKey?: string;
  strongestDoshaId?: string;
  strongestDoshaSeverity?: number;
  /**
   * When the offer's tappable CTA is lazy-loaded (`ssr:false` island), pass the
   * card's mounted state here so `offer_seen` is ARMED only once the button is
   * actually present. Without this, a 0×0 sentinel at `threshold:0` fires the
   * impression while the CTA chunk is still fetching — a tap in that window is a
   * dead click and the inflated denominator makes offer_seen→click read a false
   * low (the rule-46/47 honest-impression contract). Omit (undefined) → armed
   * immediately, preserving behaviour for server-rendered offer surfaces.
   */
  ready?: boolean;
  /** Optional actual offer-card element. When supplied, visibility is measured
   * against its painted area instead of the legacy zero-size sentinel. */
  targetRef?: RefObject<Element | null>;
}) {
  const sentinelRef = useRef<HTMLSpanElement | null>(null);
  const observedRef = targetRef ?? sentinelRef;
  useImpressionWhenVisible(
    // Hold the key null until the CTA has mounted (ready !== false); a null key
    // makes useImpressionWhenVisible WAIT — it never drops the impression, it
    // just defers it to the moment the button exists (fires once, then).
    ready === false ? null : (presentationKey ?? `${planId}|${placement}|${sourcePath}`),
    () => {
      trackAgentRevenueOfferShown({
        offer_type: "report",
        plan_id: planId,
        source_path: sourcePath,
        landing_path: landingPath,
        landing_intent: landingIntent,
        intent,
        placement,
        checkout_channel: ISHVARAM_REPORT_CHECKOUT_CHANNEL,
        commerce_system: ISHVARAM_COMMERCE_SYSTEM,
        customer_problem: customerProblem,
        urgency,
        confidence,
        route_rank_score: routeRankScore,
        route_rank_reason: routeRankReason,
        ranked_offer_order: rankedOfferOrder ?? [planId],
        strongest_dosha_id: strongestDoshaId,
        strongest_dosha_severity: strongestDoshaSeverity,
      });
    },
    observedRef,
    // A sentinel point is 0-area, so gate on entering view (threshold 0), held
    // briefly so a fast scroll-past doesn't count.
    // persist: dedup across remounts within the session — the key carries
    // planId+placement+readingId+sourcePath, so it stays per-presentation unique
    // and can never re-inflate offer_seen on a chat/list re-render (rule 47).
    targetRef
      ? { threshold: 0.5, dwellMs: 1000, persist: true }
      : { threshold: 0, dwellMs: 800, persist: true }
  );
  // 0×0 invisible sentinel at the offer's location — the thing whose visibility
  // means "the offer scrolled into the seeker's view". aria-hidden + zero-box so
  // it never affects layout or a11y.
  return targetRef ? null : (
    <span ref={sentinelRef} aria-hidden="true" style={{ width: 0, height: 0 }} />
  );
}
